import { MEILI_SEARCH_CONFIG_KEY } from '@thyme/libs/src/meiliSearch/config'
import { getResults } from '@thyme/libs/src/meiliSearch/queries'
import { SearchResult } from '@thyme/libs/src/meiliSearch/types'
import debounce from 'lodash-es/debounce'
import { computed, ExtractPropTypes, inject, ref } from 'vue'
import { SEARCH_PROPS } from './types'

type MeiliSearchInstantSearchProps = ExtractPropTypes<typeof SEARCH_PROPS>

const USER_SEARCH_EVENT_WAIT_TIME_MS = 1000

/**
 * Setup function for MeiliSearchInstantSearch
 * @param props
 */
export function setup(props: MeiliSearchInstantSearchProps) {
  const meiliConfig = inject(MEILI_SEARCH_CONFIG_KEY)
  if (!meiliConfig) {
    throw new Error('MeiliSearch config not provided')
  }

  const indexName = 'article'
  const highlightSplitTag = '__THYME-HIGHLIGHT__'

  const suggestions = ref<SearchResult[]>([])
  const isLoading = ref(false)

  const searchIcon = computed(() => (isLoading.value ? 'loading' : 'search'))

  // used to differentiate no search from no results
  const currentQuery = ref('')

  // show "no results found" message
  const showEmpty = computed(
    () => !isLoading.value && !suggestions.value.length && !!currentQuery.value
  )

  const update = async (query: string) => {
    suggestions.value = await getResults(
      meiliConfig,
      query,
      indexName,
      highlightSplitTag
    )
    isLoading.value = false
  }
  const USER_INPUT_WAIT_TIME = 200
  const debouncedUpdate = debounce(update, USER_INPUT_WAIT_TIME)

  // Create debounced event logSearchEvent if provided
  const debouncedLogSearchEvent = props.logSearchEvent
    ? debounce(props.logSearchEvent, USER_SEARCH_EVENT_WAIT_TIME_MS)
    : null

  const onInput = (query: string) => {
    isLoading.value = true
    currentQuery.value = query
    void debouncedUpdate(query)

    if (debouncedLogSearchEvent) {
      void debouncedLogSearchEvent(query)
    }
  }

  return {
    searchIcon,
    indexName,
    highlightSplitTag,
    suggestions,
    showEmpty,
    onInput,
    // for tests only
    currentQuery,
    debouncedUpdate,
  }
}
